<template>
  <v-radio-group
    :data-testid="sectionTag"
    :aria-label="accessibleName"
    :aria-labelledby="accessibleNameId"
    hide-details
  >
    <slot name="default"></slot>
  </v-radio-group>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

export interface WRadioGroupProps {
  sectionTag?: string
  /**
   * The group's accessible name. Provide exactly one of `ariaLabel` or `ariaLabelledby` — a
   * `role="radiogroup"` with neither is announced unnamed, so the individual radios carry no shared
   * context. Use `ariaLabelledby` when the question is already visible on the page.
   */
  ariaLabel?: string
  ariaLabelledby?: string
}

const props = withDefaults(defineProps<WRadioGroupProps>(), {
  sectionTag: 'unspecified',
  ariaLabel: undefined,
  ariaLabelledby: undefined
})

const hasAriaLabel = computed(() => Boolean(props.ariaLabel?.trim()))
const hasAriaLabelledby = computed(() => Boolean(props.ariaLabelledby?.trim()))

// Mutually exclusive by construction rather than by convention: ARIA lets `aria-labelledby` win
// silently, so emitting both would discard `ariaLabel` with only a dev warning to show for it.
const accessibleName = computed(() => (hasAriaLabelledby.value ? undefined : props.ariaLabel))
const accessibleNameId = computed(() => (hasAriaLabelledby.value ? props.ariaLabelledby : undefined))

onMounted(() => {
  if (!import.meta.env.DEV) return

  if (!hasAriaLabel.value && !hasAriaLabelledby.value) {
    console.warn(`[WRadioGroup] (sectionTag: "${props.sectionTag}") Provide either ariaLabel or ariaLabelledby.`)
  } else if (hasAriaLabel.value && hasAriaLabelledby.value) {
    console.warn(`[WRadioGroup] (sectionTag: "${props.sectionTag}") Provide ariaLabel or ariaLabelledby, not both.`)
  }
})
</script>

<style scoped></style>
